import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import FeatureGrid from "@/components/FeatureGrid";
import SeoContent from "@/components/SeoContent";
import { HOME } from "@/constants/testIds";
import useSeo from "@/hooks/useSeo";
import { istTodayISO, formatDMY } from "@/lib/date";
import { findKeywordConfig } from "@/constants/keywordLandings";

const DEFAULT_SEO = {
  title: "Official Morning Teer | Morning Teer Official Result",
  ogTitle: "Official Morning Teer | Morning Teer Official Result",
  description:
    "Check Morning Teer Result Today and Shillong Morning Teer FR and SR results. View common numbers, target numbers, house ending, previous results and result charts.",
  ogDescription:
    "Check Morning Teer Result Today, Shillong Morning Teer Result, Morning Teer Common Number, Previous Results, House Ending and Result Charts — updated daily.",
  h1: "Morning Teer Result Today — Shillong Morning Teer Live FR & SR Result",
};

export default function Home() {
  const { pathname } = useLocation();
  const kw = findKeywordConfig(pathname);
  useSeo({
    title: kw?.title || DEFAULT_SEO.title,
    ogTitle: kw?.title || DEFAULT_SEO.ogTitle,
    description: kw?.description || DEFAULT_SEO.description,
    ogDescription: kw?.description || DEFAULT_SEO.ogDescription,
    path: pathname,
  });

  const [data, setData] = useState({
    result: null,
    settings: { r1_label: "10:30 AM", r2_label: "11:30 AM", site_title: "Official Morning Teer" },
  });

  useEffect(() => {
    api
      .get("/results/latest")
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);

  const r = data.result;
  const today = istTodayISO();
  const isToday = r?.date === today;
  // Show the date of the published result if it exists; fall back to today
  const displayDate = r?.date || today;
  const resultDateLabel =
    r?.date && r.date !== today ? `Last result · ${formatDMY(r.date)}` : null;

  return (
    <>
      {/* SEO H1 (visually hidden — visible brand is in site header) */}
      <h1 className="sr-only">
        {kw?.h1 || DEFAULT_SEO.h1}
      </h1>

      {/* Today's date */}
      <div className="max-w-xl mx-auto w-full px-4 pt-4 text-center">
        <div className="text-lg sm:text-xl font-bold text-slate-900">{formatDMY(displayDate)}</div>
      </div>

      {/* Result Card */}
      <div className="max-w-xl mx-auto w-full px-4 mt-3 relative">
        <motion.div
          data-testid={HOME.resultCard}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="relative rounded-lg bg-white shadow-md overflow-hidden ring-1 ring-slate-300"
        >
          <div className="bg-slate-100 text-slate-900 text-center py-2 border-b border-slate-300">
            <div className="text-base font-heading font-black tracking-[0.18em]">SHILLONG</div>
          </div>
          <div className="grid grid-cols-2 bg-emerald-500 text-white border-b border-slate-300">
            <div data-testid={HOME.r1Label} className="text-center py-2 border-r border-white/40">
              <span className="text-sm sm:text-base font-heading font-black">
                F/R ({data.settings.r1_label})
              </span>
            </div>
            <div data-testid={HOME.r2Label} className="text-center py-2">
              <span className="text-sm sm:text-base font-heading font-black">
                S/R ({data.settings.r2_label})
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 divide-x divide-slate-300 bg-white relative">
            {isToday && (
              <div className="absolute top-2 right-2 flex items-center gap-1 z-10">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-[9px] font-bold uppercase tracking-widest text-emerald-600">
                  Live
                </span>
              </div>
            )}
            <div
              data-testid={HOME.r1Value}
              className="font-numbers font-black text-4xl sm:text-5xl text-center py-4 text-slate-900 animate-number-in"
            >
              {r?.r1 ? String(r.r1).padStart(2, "0") : "--"}
            </div>
            <div
              data-testid={HOME.r2Value}
              className="font-numbers font-black text-4xl sm:text-5xl text-center py-4 text-slate-900 animate-number-in"
            >
              {r?.r2 ? String(r.r2).padStart(2, "0") : "--"}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Grid */}
      <div className="max-w-xl mx-auto w-full px-4 pt-8 pb-4">
        <div className="flex items-baseline justify-between mb-4 px-1">
          <div className="font-heading font-bold text-slate-900 text-lg">Explore</div>
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Menu
          </span>
        </div>
        <FeatureGrid />
      </div>

      <SeoContent />
    </>
  );
}
